import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  deleteCertificateService,
  getCertificateCSVService,
  getCertificatePdfBatchService,
  getCertificatePDFByIdService,
  resolveCertificateSubmitInfoService,
  resubmitCertificateService,
  updateCertificateService,
} from '@/services/httpServices/CertificateServices';
import type {
  DeleteCertificateServiceConfig,
  GetCertificateCSVConfig,
  GetCertificatePdfBatchConfig,
  GetCertificatePdfByIdConfig,
  ResolveCertificateSubmitInfoServiceConfig,
  ResubmitCertificateServiceConfig,
  UpdateCertificateServiceConfig,
} from '@/services/httpServices/CertificateServices/types';

import { profileListKeys } from '../queries/profileQueryKeys';
import { certificateListKeys } from '../queries/useCertificateQueryKeys';

export function useDeleteCertificateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteCertificateServiceConfig) => {
      try {
        await deleteCertificateService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: certificateListKeys(),
      });
    },
  });
}

export function useGetCertificatePdfByIdMutation() {
  return useMutation({
    mutationFn: async (config: GetCertificatePdfByIdConfig) =>
      getCertificatePDFByIdService(config),
  });
}

export function useDownloadCertificateCSV() {
  return useMutation({
    mutationFn: async (config: GetCertificateCSVConfig) => {
      const response = await getCertificateCSVService(config);

      // Create and trigger download
      const blob = new Blob([response], {
        type: 'text/csv;charset=utf-8;',
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = `certificates-${config.params?.query?.language}-${new Date().toISOString().split('T')[0]}.csv`;

      // Set up and trigger download
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 0);
    },
  });
}

export function useGetCertificatePdfBatchMutation() {
  return useMutation({
    mutationFn: async (config: GetCertificatePdfBatchConfig) =>
      getCertificatePdfBatchService(config),
  });
}

export function useResolveCertificateSubmitInfoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ResolveCertificateSubmitInfoServiceConfig) => {
      try {
        await resolveCertificateSubmitInfoService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: certificateListKeys(),
      });
    },
  });
}

/**
 * Sends the certificate to the customer LMS again. The error is deliberately
 * not flattened into a string - the caller needs the reason to tell "we never
 * tried" from "the LMS refused it".
 */
export function useResubmitCertificateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: ResubmitCertificateServiceConfig) =>
      resubmitCertificateService(config),
    // Settled, not success: a rejected attempt has already rewritten the stored
    // errors and warnings, so the row has to be refetched on that path too or
    // it keeps rendering the message from the previous attempt.
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: certificateListKeys(),
      });
    },
  });
}

export function useUpdateCertificateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateCertificateServiceConfig) =>
      updateCertificateService(config),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: certificateListKeys(),
        }),
        queryClient.invalidateQueries({ queryKey: profileListKeys() }),
      ]);
    },
  });
}
